import client from './client';

/** Every call the app makes, grouped by feature. */
const api = {
  auth: {
    sendOtp: (body) => client.post('/auth/send-otp', body),
    verifyOtp: (body) => client.post('/auth/verify-otp', body),
    me: () => client.get('/auth/me'),
    registerPartner: (body) => client.post('/auth/partner/register', body),
    // Takes the token explicitly: signOut clears storage first, so the request
    // interceptor has nothing left to attach.
    logout: (token) => client.post('/auth/logout', {}, token
      ? { headers: { Authorization: `Bearer ${token}` } }
      : undefined),
    deleteAccount: () => client.delete('/auth/account'),
    // Deleting from the website, with no sign-in - what Google Play requires.
    startDeletion: (body) => client.post('/auth/account/delete/start', body),
    confirmDeletion: (body) => client.post('/auth/account/delete/confirm', body),
  },
  app: {
    home: (params) => client.get('/home', { params }),
    search: (params) => client.get('/search', { params }),
    config: () => client.get('/config'),
  },
  user: {
    profile: () => client.get('/users/profile'),
    update: (body) => client.put('/users/profile', body),
    setLocation: (body) => client.put('/users/location', body),
    dashboard: () => client.get('/users/dashboard'),
    seekers: (params) => client.get('/users/seekers', { params }),
    seeker: (id) => client.get(`/users/seekers/${id}`),
  },
  menu: {
    // How many master entries exist for this business, per list.
    availability: () => client.get('/menu/templates/availability'),
    templates: (params) => client.get('/menu/templates', { params }),
    adopt: (body) => client.post('/menu/templates/adopt', body),
  },
  splash: {
    current: (params) => client.get('/splash/current', { params }),
  },
  garage: {
    services: (params) => client.get('/garage/services', { params }),
    garages: (params) => client.get('/garage/garages', { params }),
    vehicles: () => client.get('/garage/vehicles'),
    addVehicle: (body) => client.post('/garage/vehicles', body),
    updateVehicle: (id, body) => client.put(`/garage/vehicles/${id}`, body),
    removeVehicle: (id) => client.delete(`/garage/vehicles/${id}`),
    history: (id) => client.get(`/garage/vehicles/${id}/history`),
    book: (body) => client.post('/garage/jobs', body),
    myJobs: (params) => client.get('/garage/jobs/mine', { params }),
    job: (id) => client.get(`/garage/jobs/${id}`),
    respondToEstimate: (id, body) => client.patch(`/garage/jobs/${id}/estimate`, body),
    cancel: (id, body) => client.patch(`/garage/jobs/${id}/cancel`, body),
    // the garage side
    partnerJobs: (params) => client.get('/garage/partner/jobs', { params }),
    partnerEarnings: () => client.get('/garage/partner/earnings'),
    setStatus: (id, body) => client.patch(`/garage/partner/jobs/${id}/status`, body),
    saveInspection: (id, body) => client.post(`/garage/partner/jobs/${id}/inspection`, body),
    sendEstimate: (id, body) => client.post(`/garage/partner/jobs/${id}/estimate`, body),
    saveBill: (id, body) => client.patch(`/garage/partner/jobs/${id}/bill`, body),
  },
  settings: {
    // Taxes & extras customers are charged under - public, no login needed.
    charges: () => client.get('/settings/charges'),
    // What someone accepts before their first sign-in.
    terms: (role) => client.get('/settings/terms', { params: { role } }),
  },
  hotels: {
    list: (params) => client.get('/hotels', { params }),
    detail: (id, params) => client.get(`/hotels/${id}`, { params }),
    book: (id, body) => client.post(`/hotels/${id}/book`, body),
    myBookings: (params) => client.get('/hotels/bookings/mine', { params }),
    booking: (id) => client.get(`/hotels/bookings/${id}`),
    cancel: (id, body) => client.patch(`/hotels/bookings/${id}/cancel`, body),
    // the owner's own property
    mine: () => client.get('/hotels/mine'),
    saveMine: (body) => client.put('/hotels/mine', body),
    addRoom: (body) => client.post('/hotels/mine/rooms', body),
    updateRoom: (id, body) => client.put(`/hotels/mine/rooms/${id}`, body),
    removeRoom: (id) => client.delete(`/hotels/mine/rooms/${id}`),
    partnerBookings: (params) => client.get('/hotels/mine/bookings', { params }),
    calendar: (params) => client.get('/hotels/mine/calendar', { params }),
    earnings: () => client.get('/hotels/mine/earnings'),
    setBookingStatus: (id, body) => client.patch(`/hotels/mine/bookings/${id}/status`, body),
  },
  categories: {
    request: (body) => client.post('/categories/request', body),
    list: (params) => client.get('/categories', { params }),
    detail: (idOrSlug) => client.get(`/categories/${idOrSlug}`),
    // What this category asks of its items, and the units it sells by.
    fields: (id, params) => client.get(`/categories/${id}/fields`, { params }),
    units: (id, params) => client.get(`/categories/${id}/units`, { params }),
  },
  partners: {
    // Where settlement money is sent. Masked on the way back, even to its owner.
    bank: () => client.get('/partners/me/bank'),
    saveBank: (body) => client.put('/partners/me/bank', body),
    myCash: () => client.get('/partners/me/cash'),
    list: (params) => client.get('/partners', { params }),
    detail: (id) => client.get(`/partners/${id}`),
    me: () => client.get('/partners/me'),
    updateMe: (body) => client.put('/partners/me', body),
    saveSections: (body) => client.put('/partners/me/sections', body),
    // The shop's own categories - its shelves - and the ones KAAMWALA suggests.
    myCategories: () => client.get('/partners/me/categories'),
    masterCategories: () => client.get('/partners/me/categories/master'),
    createCategory: (body) => client.post('/partners/me/categories', body),
    updateCategory: (id, body) => client.put(`/partners/me/categories/${id}`, body),
    removeCategory: (id) => client.delete(`/partners/me/categories/${id}`),
    adoptCategories: (body) => client.post('/partners/me/categories/adopt', body),
    toggleOpen: (body) => client.patch('/partners/me/open', body),
    dashboard: () => client.get('/partners/me/dashboard'),
  },
  services: {
    list: (params) => client.get('/services', { params }),
    detail: (id) => client.get(`/services/${id}`),
    mine: (params) => client.get('/services/mine', { params }),
    create: (body) => client.post('/services', body),
    update: (id, body) => client.put(`/services/${id}`, body),
    remove: (id) => client.delete(`/services/${id}`),
  },
  products: {
    list: (params) => client.get('/products', { params }),
    detail: (id) => client.get(`/products/${id}`),
    mine: (params) => client.get('/products/mine', { params }),
    create: (body) => client.post('/products', body),
    update: (id, body) => client.put(`/products/${id}`, body),
    setStock: (id, body) => client.patch(`/products/${id}/stock`, body),
    remove: (id) => client.delete(`/products/${id}`),
  },
  cart: {
    list: () => client.get('/cart'),
    count: () => client.get('/cart/count'),
    add: (body) => client.post('/cart/add', body),
    update: (body) => client.put('/cart/item', body),
    clear: (partnerId) => client.delete(`/cart/${partnerId}`),
  },
  addresses: {
    list: () => client.get('/addresses'),
    create: (body) => client.post('/addresses', body),
    update: (id, body) => client.put(`/addresses/${id}`, body),
    setDefault: (id) => client.patch(`/addresses/${id}/default`),
    remove: (id) => client.delete(`/addresses/${id}`),
  },
  orders: {
    create: (body) => client.post('/orders', body),
    mine: (params) => client.get('/orders/mine', { params }),
    partner: (params) => client.get('/orders/partner', { params }),
    detail: (id) => client.get(`/orders/${id}`),
    setStatus: (id, body) => client.patch(`/orders/${id}/status`, body),
    findRider: (id) => client.patch(`/orders/${id}/find-rider`),
    deliveryRates: () => client.get('/orders/delivery/rate-card'),
    cancel: (id, body) => client.patch(`/orders/${id}/cancel`, body),
    setPaymentMode: (id, body) => client.patch(`/orders/${id}/payment-mode`, body),
  },
  bookings: {
    setPaymentMode: (id, body) => client.patch(`/bookings/${id}/payment-mode`, body),
    create: (body) => client.post('/bookings', body),
    mine: (params) => client.get('/bookings/mine', { params }),
    partner: (params) => client.get('/bookings/partner', { params }),
    detail: (id) => client.get(`/bookings/${id}`),
    setStatus: (id, body) => client.patch(`/bookings/${id}/status`, body),
    cancel: (id, body) => client.patch(`/bookings/${id}/cancel`, body),
    reschedule: (id, body) => client.patch(`/bookings/${id}/reschedule`, body),
  },
  jobs: {
    list: (params) => client.get('/jobs', { params }),
    detail: (id) => client.get(`/jobs/${id}`),
    apply: (id, body) => client.post(`/jobs/${id}/apply`, body),
    myApplications: (params) => client.get('/jobs/applications/mine', { params }),
    mine: (params) => client.get('/jobs/posted/mine', { params }),
    create: (body) => client.post('/jobs', body),
    close: (id) => client.patch(`/jobs/${id}/close`),
    applications: (id, params) => client.get(`/jobs/${id}/applications`, { params }),
    setApplicationStatus: (id, body) => client.patch(`/jobs/applications/${id}/status`, body),
    toggleSaved: (id) => client.post(`/jobs/${id}/save`),
    saved: () => client.get('/jobs/saved/mine'),
  },
  vehicles: {
    list: (params) => client.get('/vehicles', { params }),
    detail: (id) => client.get(`/vehicles/${id}`),
    create: (body) => client.post('/vehicles', body),
    mine: (params) => client.get('/vehicles/mine', { params }),
    markSold: (id) => client.patch(`/vehicles/${id}/sold`),
    remove: (id) => client.delete(`/vehicles/${id}`),
    enquire: (id, body) => client.post(`/vehicles/${id}/enquiry`, body),
    enquiries: () => client.get('/vehicles/enquiries/mine'),
  },
  rides: {
    setPaymentMode: (id, body) => client.patch(`/rides/${id}/payment-mode`, body),
    estimate: (body) => client.post('/rides/estimate', body),
    create: (body) => client.post('/rides', body),
    mine: (params) => client.get('/rides/mine', { params }),
    detail: (id) => client.get(`/rides/${id}`),
    cancel: (id, body) => client.patch(`/rides/${id}/cancel`, body),

    // driver
    offers: () => client.get('/rides/driver/offers'),
    active: () => client.get('/rides/driver/active'),
    history: (params) => client.get('/rides/driver/history', { params }),
    availability: (body) => client.patch('/rides/driver/availability', body),
    heartbeat: (body) => client.patch('/rides/driver/location', body),
    myLocation: () => client.get('/rides/driver/location'),
    accept: (id) => client.patch(`/rides/${id}/accept`),
    reject: (id) => client.patch(`/rides/${id}/reject`),
    arrived: (id, body) => client.patch(`/rides/${id}/arrived`, body),
    start: (id, body) => client.patch(`/rides/${id}/start`, body),
    complete: (id, body) => client.patch(`/rides/${id}/complete`, body),
    driverCancel: (id, body) => client.patch(`/rides/${id}/driver-cancel`, body),
    pushLocation: (id, body) => client.patch(`/rides/${id}/location`, body),
  },
  desires: {
    create: (body) => client.post('/desires', body),
    mine: () => client.get('/desires/mine'),
    cancel: (id) => client.patch(`/desires/${id}/cancel`),
  },
  reviews: {
    list: (params) => client.get('/reviews', { params }),
    create: (body) => client.post('/reviews', body),
    mine: () => client.get('/reviews/mine'),
  },
  notifications: {
    list: (params) => client.get('/notifications', { params }),
    unread: () => client.get('/notifications/unread-count'),
    markRead: (id) => client.patch(`/notifications/${id}/read`),
    markAllRead: () => client.patch('/notifications/read-all'),
  },
  payments: {
    initiate: (body) => client.post('/payments/initiate', body),
    verify: (body) => client.post('/payments/verify', body),
    markFailed: (body) => client.post('/payments/failed', body),
    status: (id) => client.get(`/payments/${id}`),
    doorQr: (orderId) => client.post('/payments/door', { orderId }),
    doorStatus: (paymentId) => client.get(`/payments/door/${paymentId}`),
    mine: () => client.get('/payments/mine'),
  },
  maps: {
    places: (params) => client.get('/maps/places', { params }),
    placeDetails: (placeId) => client.get(`/maps/places/${placeId}`),
    reverse: (params) => client.get('/maps/reverse', { params }),
    route: (from, to) => client.get('/maps/route', { params: { from: from.join(','), to: to.join(',') } }),
    // Public - usable during signup, before the user has an account.
    cities: (q) => client.get('/maps/cities', { params: { q } }),
    cityDetails: (placeId) => client.get(`/maps/cities/${placeId}`),
  },
  /** Property and animal classifieds - one engine, two front doors. */
  listings: {
    property: {
      list: (params) => client.get('/properties', { params }),
      detail: (id) => client.get(`/properties/${id}`),
      create: (body) => client.post('/properties', body),
      mine: () => client.get('/properties/mine'),
      close: (id) => client.patch(`/properties/${id}/close`),
      enquire: (id, body) => client.post(`/properties/${id}/enquire`, body),
    },
    /* Ordinary second-hand things: a fridge, a phone, a cycle. */
    item: {
      list: (params) => client.get('/items', { params }),
      detail: (id) => client.get(`/items/${id}`),
      create: (body) => client.post('/items', body),
      update: (id, body) => client.put(`/items/${id}`, body),
      mine: () => client.get('/items/mine'),
      close: (id) => client.patch(`/items/${id}/close`),
      remove: (id) => client.delete(`/items/${id}`),
      enquire: (id, body) => client.post(`/items/${id}/enquire`, body),
      enquiries: () => client.get('/items/enquiries'),
    },
    animal: {
      schema: () => client.get('/animals/schema'),
      list: (params) => client.get('/animals', { params }),
      detail: (id) => client.get(`/animals/${id}`),
      create: (body) => client.post('/animals', body),
      mine: () => client.get('/animals/mine'),
      close: (id) => client.patch(`/animals/${id}/close`),
      enquire: (id, body) => client.post(`/animals/${id}/enquire`, body),
    },
  },
  /**
   * Paid greetings ("special day" wishes). The customer composes, pays, admin
   * reviews, and on the chosen day it goes out to everyone.
   */
  /** Admin-defined questions partners answer when they register. */
  onboarding: {
    fields: (params) => client.get('/onboarding/fields', { params }),
  },
  wishes: {
    config: () => client.get('/wishes/config'),
    feed: () => client.get('/wishes/feed'),
    mine: () => client.get('/wishes/mine'),
    create: (body) => client.post('/wishes', body),
    update: (id, body) => client.put(`/wishes/${id}`, body),
    remove: (id) => client.delete(`/wishes/${id}`),
  },
  /** Help desk: admin-written questions, tickets and the chat on each one. */
  support: {
    topics: (appliesTo) => client.get('/support/topics', { params: { appliesTo } }),
    tickets: () => client.get('/support/tickets'),
    ticket: (id) => client.get(`/support/tickets/${id}`),
    create: (body) => client.post('/support/tickets', body),
    reply: (id, body) => client.post(`/support/tickets/${id}/messages`, body),
  },
  banners: {
    list: (params) => client.get('/banners', { params }),
  },
};

export default api;
